/**
 * §11.7 Compliance Posture / Roadmap.
 *
 * The most legally-sensitive section on the page — every row is
 * written under a strict "no overclaim" policy.  Status pills use
 * these categories only:
 *
 *   in-progress   → audit engaged, work under way
 *   roadmap       → planned, not started
 *   aligned       → aligned with the framework (no formal cert)
 *   documented    → we publish / share the artifact
 *   not-in-scope  → the framework does not apply to us
 */

import React from 'react'
import clsx from 'clsx'
import {
  ShieldCheck, ScrollText, BadgeCheck, CreditCard, HeartPulse, Users, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { COMPLIANCE_ITEMS, COMPLIANCE_LABELS } from '../data'
import { TONE } from '../_shared'

const ICONS = { ShieldCheck, ScrollText, BadgeCheck, CreditCard, HeartPulse, Users, Sparkles }

export default function ComplianceRoadmap() {
  return (
    <section id="compliance" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="neutral">Compliance</Eyebrow>
          <SectionHeading align="center"
            title={<>Compliance posture,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">stated honestly.</span></>}
            subtitle="What we have, what is in progress, what is on our roadmap and what does not apply to us.  We would rather earn certifications than claim them."
          />
        </div>

        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {COMPLIANCE_ITEMS.map((c) => {
            const tone = TONE[c.tone] || TONE.orange
            const Icon = ICONS[c.iconKey] || ShieldCheck
            const status = COMPLIANCE_LABELS[c.status] || COMPLIANCE_LABELS['documented']
            return (
              <div key={c.id} className={clsx(
                'relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl overflow-hidden',
                'transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px]',
              )}>
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 20, height: 20 }} />
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 text-[10.5px] font-semibold text-zinc-200">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: status.dot }} />
                      {status.label}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{c.label}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-2">{c.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Vendor security questionnaire teaser */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center justify-between">
          <div className="flex items-start gap-4 max-w-3xl">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brand-gold/30 bg-brand-gold/[0.08] text-brand-gold">
              <ScrollText strokeWidth={1.8} style={{ width: 20, height: 20 }} />
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="text-[15px] font-semibold text-white">Enterprise procurement pack</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Our vendor-security questionnaire response (SIG Lite / CAIQ), pen-test summary letter and DPA are available under NDA to prospects in active procurement.
              </p>
            </div>
          </div>
          <a href="/contact?topic=security" className="self-start md:self-auto inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.06] hover:border-white/25 transition-colors">
            Request pack
          </a>
        </div>
      </Container>
    </section>
  )
}
